import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Tabs, Tab, Divider, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import RepeatIcon from "@mui/icons-material/Repeat";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import TabletIcon from "@mui/icons-material/Tablet";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import "./ComposePage.css";

const LINKEDIN_LIMIT = 3000;

const DEVICES = [
  { label: "Mobile", icon: <SmartphoneIcon sx={{ fontSize: 16 }} />, maxWidth: 360 },
  { label: "Tablet", icon: <TabletIcon sx={{ fontSize: 16 }} />,     maxWidth: 540 },
  { label: "Web",    icon: <DesktopWindowsIcon sx={{ fontSize: 16 }} />, maxWidth: "100%" },
];

const PostPreview = ({ content = "", images = [], videos = [], documents = [], userProfile }) => {
  const [deviceIndex, setDeviceIndex] = useState(0);
  const [mediaIndex, setMediaIndex] = useState(0);

  // Combine images and videos into one ordered media list for the carousel
  const allMedia = [
    ...images.map((f) => ({ file: f, type: "image" })),
    ...videos.map((f) => ({ file: f, type: "video" })),
  ];
  const mediaTotal = allMedia.length;

  // Reset carousel when media count changes
  useEffect(() => {
    setMediaIndex((i) => (i >= mediaTotal ? 0 : i));
  }, [mediaTotal]);

  const prevMedia = (e) => {
    e.stopPropagation();
    setMediaIndex((i) => (i - 1 + mediaTotal) % mediaTotal);
  };
  const nextMedia = (e) => {
    e.stopPropagation();
    setMediaIndex((i) => (i + 1) % mediaTotal);
  };

  const device    = DEVICES[deviceIndex];
  const charLeft  = LINKEDIN_LIMIT - content.length;
  const isEmpty   = content.trim() === "" && images.length === 0 && videos.length === 0 && documents.length === 0;

  const displayName = userProfile?.name || "Your Name";
  const avatarSrc   = userProfile?.profilePicture || null;
  const initials    = displayName.charAt(0).toUpperCase();

  const charCounterStyle = {
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    color: charLeft < 0 ? "#DA4040" : charLeft < 300 ? "#F7C333" : "#2e7d32",
  };

  return (
    <Box className="preview-panel">

      {/* Header */}
      <Box className="preview-header">
        <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 16, color: "#3B3B3B" }}>
          Live Preview
        </Typography>
        <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#95919D", mt: 0.3, mb: 1.5 }}>
          Updates in real time as you type
        </Typography>
      </Box>

      {/* Device tabs */}
      <Box className="preview-device-tabs">
        <Tabs
          value={deviceIndex}
          onChange={(_, v) => setDeviceIndex(v)}
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              minWidth: 80,
              fontFamily: "Poppins",
              fontSize: 12,
              fontWeight: 500,
              textTransform: "none",
              color: "#95919D",
              py: 0.75,
              px: 1.5,
              mr: "6px",
              borderRadius: "8px 8px 0 0",
              transition: "color 0.15s, background 0.15s",
            },
            "& .MuiTab-root:hover": { color: "#0047AB", background: "#F0F4FF" },
            "& .Mui-selected": { color: "#0047AB !important", fontWeight: 600 },
            "& .MuiTabs-indicator": { backgroundColor: "#0047AB", height: "2px" },
          }}
        >
          {DEVICES.map((d) => (
            <Tab
              key={d.label}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {d.icon}
                  <span>{d.label}</span>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      <Divider sx={{ borderColor: "#DFDFDF" }} />

      {/* Preview frame */}
      <Box className="preview-frame-area">
        <Box sx={{
          width: "100%",
          maxWidth: device.maxWidth,
          transition: "max-width 0.3s ease",
          mx: "auto",
          ...(deviceIndex < 2 && {
            boxShadow: "0 0 0 2px #DFDFDF, 0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: "12px",
          }),
        }}>
          {isEmpty ? (
            <Box className="preview-empty">
              <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: "#95919D" }}>
                Start writing your post to see a live preview here.
              </Typography>
            </Box>
          ) : (
            <Box className="preview-card">

              {/* Post author */}
              <Box sx={{ display: "flex", alignItems: "flex-start", p: 2, gap: 1.5 }}>
                <Avatar
                  src={avatarSrc}
                  sx={{ width: 42, height: 42, bgcolor: "#0047AB", fontSize: 17, fontWeight: 700 }}
                >
                  {!avatarSrc && initials}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#000" }}>
                      {displayName}
                    </Typography>
                    <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#0047AB", fontWeight: 600 }}>
                      • 1st
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: "Poppins", fontSize: 11, color: "#95919D" }}>
                    Just now · 🌐
                  </Typography>
                </Box>
              </Box>

              {/* Post content */}
              <Box sx={{ px: 2, pb: images.length > 0 ? 1.5 : 2 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: deviceIndex === 0 ? 13 : 14,
                    lineHeight: 1.65,
                    color: "#3B3B3B",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {content}
                </Typography>
              </Box>

              {/* Media carousel */}
              {mediaTotal > 0 && (
                <Box className="preview-card-media">
                  {/* Current media item */}
                  {allMedia[mediaIndex]?.type === "image" ? (
                    <img
                      key={mediaIndex}
                      src={URL.createObjectURL(allMedia[mediaIndex].file)}
                      alt={`preview ${mediaIndex + 1}`}
                      style={{ width: "100%", height: 260, objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <video
                      key={mediaIndex}
                      src={URL.createObjectURL(allMedia[mediaIndex].file)}
                      controls
                      style={{ width: "100%", height: 260, objectFit: "cover", display: "block", background: "#000" }}
                    />
                  )}

                  {/* Arrow navigation */}
                  {mediaTotal > 1 && (
                    <>
                      <IconButton
                        onClick={prevMedia}
                        size="small"
                        sx={{
                          position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.45)", color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                          width: 28, height: 28,
                        }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={nextMedia}
                        size="small"
                        sx={{
                          position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                          bgcolor: "rgba(0,0,0,0.45)", color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                          width: 28, height: 28,
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      </IconButton>

                      {/* Dot indicators */}
                      <Box sx={{
                        position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
                        display: "flex", gap: "5px",
                      }}>
                        {allMedia.map((_, i) => (
                          <Box
                            key={i}
                            onClick={(e) => { e.stopPropagation(); setMediaIndex(i); }}
                            sx={{
                              width: i === mediaIndex ? 16 : 6,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: i === mediaIndex ? "#fff" : "rgba(255,255,255,0.5)",
                              cursor: "pointer",
                              transition: "width 0.2s, background 0.2s",
                            }}
                          />
                        ))}
                      </Box>

                      {/* Counter badge */}
                      <Box sx={{
                        position: "absolute", top: 8, right: 10,
                        bgcolor: "rgba(0,0,0,0.6)", color: "#fff",
                        borderRadius: "4px", px: "6px", py: "2px",
                        fontFamily: "Poppins", fontSize: 11, fontWeight: 600,
                        lineHeight: "16px", whiteSpace: "nowrap",
                        zIndex: 2,
                      }}>
                        {mediaIndex + 1} / {mediaTotal}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {documents.length > 0 && (
                <Box sx={{ p: 2, bgcolor: "#F8F9FA", borderTop: "1px solid #EDEFF2", display: "flex", flexDirection: "column", gap: 1 }}>
                  {documents.map((doc, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 36, height: 36, bgcolor: "#E2E8F0", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: 18 }}>📄</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: "#3B3B3B", fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {doc.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* LinkedIn action bar */}
              <Divider sx={{ mx: 2, borderColor: "#EDEFF2" }} />
              <Box sx={{ display: "flex", justifyContent: "space-around", py: 0.5 }}>
                {[
                  { icon: <ThumbUpAltOutlinedIcon sx={{ fontSize: 18 }} />, label: "Like" },
                  { icon: <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />,  label: "Comment" },
                  { icon: <RepeatIcon sx={{ fontSize: 18 }} />,             label: "Repost" },
                  { icon: <SendOutlinedIcon sx={{ fontSize: 18 }} />,       label: "Send" },
                ].map(({ icon, label }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      py: 0.8,
                      px: 1,
                      borderRadius: "8px",
                      color: "#95919D",
                      cursor: "default",
                    }}
                  >
                    {icon}
                    {deviceIndex !== 0 && (
                      <Typography sx={{ fontFamily: "Poppins", fontSize: 11, mt: 0.2 }}>
                        {label}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>

            </Box>
          )}
        </Box>
      </Box>

      {/* Character counter footer */}
      <Box className="preview-char-footer">
        <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#95919D" }}>
          LinkedIn limit
        </Typography>
        <Typography sx={charCounterStyle}>
          {charLeft < 0
            ? `${Math.abs(charLeft)} over limit`
            : `${charLeft.toLocaleString()} remaining`}
        </Typography>
      </Box>

    </Box>
  );
};

export default PostPreview;
